import { useCallback, useMemo, useState } from 'react'
import QUERY_KEYS from '../../graphql/queryKeys'
import publicTermsAndConditionQuery from '../../graphql/queries/publicTermsAndCondition'
import signTermsAndConditionMutation from '../../graphql/mutations/signTermsAndCondition'
import useNonAuthQuery from '../../hooks/useNonAuthQuery'
import useMutation from '../../hooks/useMutation'
import { captureError, captureErrorAndNotify } from '../../utilities/errorHandlers'
import { UNABLE_TO_REFRESH_MESSAGE, UNABLE_TO_SIGN_MESSAGE } from './constants'
import { mapSignError, resolvePageState } from './utils'

export default function useCustomerAgreement (token) {
  const [signerName, setSignerName] = useState('')
  const [accepted, setAccepted] = useState(false)
  const [formError, setFormError] = useState('')
  const [signedOverride, setSignedOverride] = useState(null)

  const { data, isLoading, isError, refetch } = useNonAuthQuery(
    [QUERY_KEYS.publicTermsAndCondition, token],
    token,
    publicTermsAndConditionQuery,
    {
      enabled: Boolean(token),
      retry: false,
      refetchOnWindowFocus: false,
      onError (error) {
        captureError(error)
      }
    }
  )

  const refreshAgreement = useCallback(({ notifyOnFailure = false } = {}) => {
    refetch().catch((error) => {
      if (notifyOnFailure) {
        captureErrorAndNotify(error, UNABLE_TO_REFRESH_MESSAGE, 6)
        return
      }
      captureError(error)
    })
  }, [refetch])

  const handleSignError = useCallback((error) => {
    const mapped = mapSignError(error)
    setFormError(mapped.formError)
    if (mapped.signedOverride) {
      setSignedOverride(mapped.signedOverride)
    }
    if (mapped.refetch) {
      refreshAgreement({ notifyOnFailure: true })
    }
  }, [refreshAgreement])

  const { mutate: signAgreement, isLoading: isSigning } = useMutation(signTermsAndConditionMutation, {
    onSuccess (result) {
      if (result?.success && result.clientTermsAndCondition) {
        setFormError('')
        setSignedOverride(result.clientTermsAndCondition)
        refreshAgreement()
        return
      }
      setFormError(UNABLE_TO_SIGN_MESSAGE)
    },
    onError (error) {
      handleSignError(error)
    }
  })

  const agreement = useMemo(() => {
    const queried = data?.publicTermsAndCondition
    if (!queried && !signedOverride) return null
    return {
      ...queried,
      ...signedOverride
    }
  }, [data, signedOverride])

  const pageState = resolvePageState({ token, isLoading, isError, agreement })
  const canSign = signerName.trim().length > 0 && accepted && !isSigning

  function handleSubmit (event) {
    event.preventDefault()
    const name = signerName.trim()
    if (!name || !accepted) return

    setFormError('')
    signAgreement({
      token,
      signerName: name,
      accepted: true
    })
  }

  return {
    pageState,
    agreement,
    signerName,
    setSignerName,
    accepted,
    setAccepted,
    formError,
    isSigning,
    canSign,
    handleSubmit
  }
}
